
import {EventDispatcher} from "../EventDispatcher.js";

import {t} from "./i18n/index.js";
// Organisation af registreringer i brugerdefinerede mapper (Registreringer-
// træet). Mapper er ORGANISATIONS-metadata om registreringerne - de er ikke
// features og ligger ikke i FeatureManager. En mappe refererer aldrig til et
// feature-objekt, kun til feature-id'er.
//
// Struktur: hver "beholder" har EN ordnet liste af søskende, hvor mapper og
// registreringer indgår blandet (rækkefølgen bestemmes af brugeren, ikke af
// typen). Beholdere:
//   - TOP:       topniveau-mapper (kun mapper)
//   - UNFILED:   registreringer uden mappe = "Uden mappe" (kun registreringer)
//   - en mappe:  blandet liste af undermapper og registreringer
// Elementerne er nøgler "d:<mappeId>" / "f:<featureId>". Den ordnede liste
// er den eneste sandhed om både placering og rækkefølge; alt andet (fx
// hvilken mappe en registrering ligger i) udledes deraf.
//
// Livscyklus: modulet ejer denne manager ved siden af FeatureManager. Den
// lytter på featureManagers "feature_added" (ny registrering lægges sidst i
// den aktive mappe, ellers "Uden mappe") og "feature_removed" (fjernes fra
// sin liste), så den lever og dør med registreringerne uden UI-afhængighed.
//
// Aktiv mappe: valgfri mappe, som nye registreringer lander i (i stedet for
// "Uden mappe"). Nulstilles, når mappen slettes.
//
// Persistens: der findes i dag INGEN gemning af selve registreringerne, så
// heller ikke af mapperne - serialize()/load() er forberedelsen, ingen
// gemning er implementeret (bevidst ingen localStorage-genvej).

let counter = 0;

export const TOP = "_top";
export const UNFILED = "_unfiled";

const fkey = (id) => `d:${id}`;
const ekey = (id) => `f:${id}`;

export class FolderManager extends EventDispatcher{
	constructor(featureManager){
		super();

		this.featureManager = featureManager;
		this.folders = [];              // [{id, name, parentId|null}] i oprettelsesrækkefølge
		this._byId = new Map();
		this._children = new Map([[TOP, []], [UNFILED, []]]); // beholder -> [nøgle]
		this.activeFolderId = null;

		this._onFeatureAdded = (e) => {
			const key = ekey(e.feature.id);
			if(this._findContainer(key) !== null) return;
			// Registreringer/Målinger (2026-10): en aktiv mappe bruges kun, hvis
			// dens egen `kind` enten matcher featurens, eller er null (legacy-
			// mappe fra før kind fandtes - permissiv, rammer intet eksisterende).
			// Et EKSPLICIT misforhold (fx "Ledninger" er registration, featuren
			// er en måling) falder tilbage til "Uden mappe", IKKE ind i den
			// forkerte mappe - se createFolder()/moveFeature()'s samme regel.
			const activeFolder = this.activeFolderId ? this._byId.get(this.activeFolderId) : null;
			const kindOk = activeFolder && (activeFolder.kind == null || activeFolder.kind === e.feature.kind);
			const target = kindOk ? this.activeFolderId : UNFILED;
			this._list(target).push(key);
			this._changed();
		};
		this._onFeatureRemoved = (e) => {
			const key = ekey(e.feature.id);
			const container = this._findContainer(key);
			if(container === null) return;
			this._remove(container, key);
			this._changed();
		};
		if(featureManager){
			featureManager.addEventListener("feature_added", this._onFeatureAdded);
			featureManager.addEventListener("feature_removed", this._onFeatureRemoved);
			// registreringer der allerede findes, da manageren oprettes
			for(const f of featureManager.features) this._children.get(UNFILED).push(ekey(f.id));
		}
	}

	_changed(){
		this.dispatchEvent({type: "folders_changed"});
	}

	_list(container){
		if(!this._children.has(container)) this._children.set(container, []);
		return this._children.get(container);
	}

	_remove(container, key){
		const list = this._children.get(container);
		const i = list ? list.indexOf(key) : -1;
		if(i !== -1) list.splice(i, 1);
		return i;
	}

	_findContainer(key){
		for(const [container, list] of this._children){
			if(list.includes(key)) return container;
		}
		return null;
	}

	_folderContainer(parentId){
		return parentId === null ? TOP : parentId;
	}

	get(folderId){
		return this._byId.get(folderId) || null;
	}

	// null = "Uden mappe"
	getFolderId(featureId){
		const container = this._findContainer(ekey(featureId));
		return container === null || container === UNFILED ? null : container;
	}

	// Ordnet liste af søskende i en beholder (folderId, eller null for
	// topniveauet hhv. "Uden mappe" afhængigt af `kind`): [{type, id}]
	getChildren(containerId){
		return this._list(containerId).map((k) => ({type: k[0] === "d" ? "folder" : "feature", id: k.slice(2)}));
	}

	// Ren data til visningen: {beholder: [nøgler]} (kopi).
	getOrder(){
		const out = {};
		for(const [container, list] of this._children) out[container] = list.slice();
		return out;
	}

	// Alle registreringer (id'er) i en mappe og dens undermapper, rekursivt.
	getFeatureIdsIn(folderId){
		const out = [];
		const walk = (id) => {
			for(const key of this._list(id)){
				if(key[0] === "f") out.push(key.slice(2));
				else walk(key.slice(2));
			}
		};
		if(this._byId.has(folderId)) walk(folderId);
		return out;
	}

	// Er `folderId` identisk med, eller en (direkte/indirekte) undermappe af, `ancestorId`?
	isSameOrDescendant(folderId, ancestorId){
		let current = folderId;
		const seen = new Set();
		while(current){
			if(current === ancestorId) return true;
			if(seen.has(current)) return false;
			seen.add(current);
			const f = this._byId.get(current);
			current = f ? f.parentId : null;
		}
		return false;
	}

	// Et unikt standardnavn ("Ny mappe", "Ny mappe 2", ...) blandt søskende.
	_uniqueName(base, parentId){
		const taken = new Set(this.folders.filter(f => f.parentId === parentId).map(f => f.name));
		if(!taken.has(base)) return base;
		let n = 2;
		while(taken.has(`${base} ${n}`)) n++;
		return `${base} ${n}`;
	}

	_insert(container, key, index){
		const list = this._list(container);
		const i = index === null || index === undefined ? list.length : Math.max(0, Math.min(index, list.length));
		list.splice(i, 0, key);
	}

	// `pointcloudId`: hvilken model-root (Registreringer-traeets nye
	// oeverste niveau) mappen hoerer under - samme stabile id-begreb som
	// Feature.pointcloudId, sat af kalderen fra den aktive model (Vue-
	// toolbarens modelkontrol), IKKE udledt her. null for en mappe oprettet
	// foer denne relation fandtes, eller hvis ingen model var aktiv -
	// traeopbygningen placerer den da under det globale "Uden mappe/model"-
	// fallback-niveau, ligesom en registrering uden pointcloudId.
	// `kind`: 'registration' | 'measurement' | null (Registreringer/Målinger-
	// opdelingen, 2026-10) - hvilken semantisk systemgruppe mappen hoerer
	// under, sat af kalderen (hvilken gruppe/mappe "Opret mappe" blev brugt
	// fra), IKKE udledt her - samme moenster som pointcloudId. null for en
	// mappe oprettet foer denne relation fandtes; traeopbygningen udleder da
	// en visningsplacering af mappens faktiske indhold (se registreringer-
	// tree.js), uden at skrive noget tilbage her.
	createFolder(name, parentId = null, index = null, pointcloudId = null, kind = null){
		if(parentId !== null && !this._byId.has(parentId)) return null;
		counter++;
		const folder = {
			id: `folder_${Date.now().toString(36)}_${counter}`,
			name: (name && String(name).trim()) || this._uniqueName(t("Ny mappe"), parentId),
			parentId,
			pointcloudId: pointcloudId != null ? pointcloudId : null,
			kind: kind != null ? kind : null,
		};
		this.folders.push(folder);
		this._byId.set(folder.id, folder);
		this._children.set(folder.id, []);
		this._insert(this._folderContainer(parentId), fkey(folder.id), index);
		this._changed();
		return folder;
	}

	renameFolder(folderId, name){
		const folder = this._byId.get(folderId);
		const clean = name && String(name).trim();
		if(!folder || !clean || folder.name === clean) return false;
		folder.name = clean;
		this._changed();
		return true;
	}

	// Flytter en mappe (med alt indhold) ind i en anden mappe eller til
	// topniveauet (newParentId === null), på position `index` (null = sidst).
	// Afviser at flytte en mappe ind i sig selv eller en af sine egne
	// undermapper (ville skabe en løkke). Samme beholder = omrokering.
	moveFolder(folderId, newParentId, index = null){
		const folder = this._byId.get(folderId);
		if(!folder) return false;
		if(newParentId !== null && !this._byId.has(newParentId)) return false;
		if(newParentId !== null && this.isSameOrDescendant(newParentId, folderId)) return false;
		// Registreringer/Målinger (2026-10): samme invariant-vagt som
		// moveFeature()'s pointcloudId-tjek - en mappe maa ikke stille flyttes
		// ind i en anden mappe med en ANDEN, eksplicit kind (null = legacy,
		// permissiv). Kun relevant naar der flyttes IND i en rigtig mappe;
		// topniveau-omrokering (newParentId === null) haandhaeves alene af
		// frontendens resolveDrop() - samme asymmetri som allerede gaelder
		// pointcloudId her (intet tilsvarende tjek fandtes for det heller).
		if(newParentId !== null){
			const newParent = this._byId.get(newParentId);
			if(folder.kind != null && newParent.kind != null && folder.kind !== newParent.kind) return false;
		}
		const key = fkey(folderId);
		const from = this._folderContainer(folder.parentId);
		const to = this._folderContainer(newParentId);
		const oldIndex = this._remove(from, key);
		let at = index;
		if(from === to && at !== null && oldIndex < at) at--;
		if(from === to && (at === null ? oldIndex === this._list(to).length : at === oldIndex)){
			this._list(to).splice(oldIndex, 0, key); // uændret plads
			return false;
		}
		folder.parentId = newParentId;
		this._insert(to, key, at);
		this._changed();
		return true;
	}

	// folderId === null: "Uden mappe". Samme beholder = omrokering.
	// Invariant-vagt (udover den primære frontend-kontrol i resolveDrop()):
	// en registrering maa aldrig havne i en navngivet mappe, der hoerer til
	// en ANDEN model end registreringen selv - almindelig organisering maa
	// ikke stiltiende aendre model-ejerskab. Afvises her hvis begge
	// pointcloudId'er er kendte (non-null) og forskellige; ingen
	// begraensning paa at flytte til/fra "Uden mappe" (folderId === null),
	// som er model-uafhaengig i selve datamodellen - traeopbygningen
	// partitionerer den visuelt pr. model ud fra registreringens EGEN
	// pointcloudId, ikke ud fra mappe-beholderen.
	moveFeature(featureId, folderId, index = null){
		const feature = this.featureManager && this.featureManager.get(featureId);
		if(!feature) return false;
		if(folderId !== null){
			const folder = this._byId.get(folderId);
			if(!folder) return false;
			if(feature.pointcloudId != null && folder.pointcloudId != null && feature.pointcloudId !== folder.pointcloudId) return false;
			// Registreringer/Målinger (2026-10): samme regel som pointcloudId
			// ovenfor - en registrering maa ikke stille organiseres ind i en
			// mappe for den ANDEN systemgruppe (null = legacy mappe, permissiv).
			if(feature.kind != null && folder.kind != null && feature.kind !== folder.kind) return false;
		}
		const key = ekey(featureId);
		const from = this._findContainer(key);
		const to = folderId === null ? UNFILED : folderId;
		const oldIndex = from === null ? -1 : this._remove(from, key);
		let at = index;
		if(from === to && at !== null && oldIndex < at) at--;
		if(from === to && (at === null ? oldIndex === this._list(to).length : at === oldIndex)){
			this._list(to).splice(oldIndex, 0, key);
			return false;
		}
		this._insert(to, key, at);
		this._changed();
		return true;
	}

	// "Slet kun mappen": kun mappen fjernes. Dens direkte indhold (registreringer
	// og undermapper) flyttes ét niveau op og ind på mappens egen plads i
	// forælderens rækkefølge. Der slettes aldrig registreringer.
	deleteFolder(folderId){
		const folder = this._byId.get(folderId);
		if(!folder) return false;
		const parentId = folder.parentId;
		const parentContainer = this._folderContainer(parentId);
		const key = fkey(folderId);
		const parentList = this._list(parentContainer);
		const at = parentList.indexOf(key);
		const contents = this._list(folderId).slice();

		// Registreringer kan ikke ligge på topniveauet - de hører til "Uden mappe".
		const lift = [];
		for(const k of contents){
			if(k[0] === "d"){
				this._byId.get(k.slice(2)).parentId = parentId;
				lift.push(k);
			}else if(parentContainer === TOP){
				this._list(UNFILED).push(k);
			}else{
				lift.push(k);
			}
		}
		parentList.splice(at, 1, ...lift);
		this._dropFolder(folderId);
		this._changed();
		return true;
	}

	_dropFolder(folderId){
		this.folders = this.folders.filter(f => f.id !== folderId);
		this._byId.delete(folderId);
		this._children.delete(folderId);
		if(this.activeFolderId === folderId) this.activeFolderId = null;
	}

	// Fjerner mappen og ALLE undermapper (kun strukturen). Returnerer id'erne
	// på de registreringer, der lå i undertræet: kalderen skal slette dem via
	// den autoritative feature-livscyklus (DigitizationModule.removeFeature) -
	// denne manager rører aldrig features/geometri. Se
	// DigitizationModule.deleteFolderAndContents().
	deleteFolderTree(folderId){
		const folder = this._byId.get(folderId);
		if(!folder) return [];
		const featureIds = this.getFeatureIdsIn(folderId);
		const collect = (id) => {
			const ids = [id];
			for(const k of this._list(id)) if(k[0] === "d") ids.push(...collect(k.slice(2)));
			return ids;
		};
		const doomed = collect(folderId);
		this._remove(this._folderContainer(folder.parentId), fkey(folderId));
		for(const id of doomed) this._dropFolder(id);
		// registreringerne fjernes normalt af feature_removed, men sikr ikke-slettede
		for(const fid of featureIds){
			const key = ekey(fid);
			if(this._findContainer(key) === null && this.featureManager && this.featureManager.get(fid)) this._list(UNFILED).push(key);
		}
		this._changed();
		return featureIds;
	}

	// Aktiv mappe: nye registreringer lander her (null = "Uden mappe").
	setActiveFolder(folderId){
		const id = folderId && this._byId.has(folderId) ? folderId : null;
		if(this.activeFolderId === id) return false;
		this.activeFolderId = id;
		this._changed();
		return true;
	}

	// Ren data til fremtidig gemning (sammen med registreringerne).
	serialize(){
		return {
			version: 2,
			folders: this.folders.map(f => ({id: f.id, name: f.name, parentId: f.parentId, pointcloudId: f.pointcloudId ?? null, kind: f.kind ?? null})),
			order: this.getOrder(),
			activeFolderId: this.activeFolderId,
		};
	}

	// Indlæser serialize()-data. Ugyldige poster (ukendt forælder, ukendt
	// mappe, ukendt registrering, løkker, dubletter) springes over frem for at
	// afvise alt; manglende registreringer lægges sidst i "Uden mappe".
	load(data){
		this.folders = [];
		this._byId.clear();
		this._children = new Map([[TOP, []], [UNFILED, []]]);
		this.activeFolderId = null;

		if(data && Array.isArray(data.folders)){
			for(const f of data.folders){
				if(!f || typeof f.id !== "string" || typeof f.name !== "string" || this._byId.has(f.id)) continue;
				const folder = {id: f.id, name: f.name, parentId: typeof f.parentId === "string" ? f.parentId : null, pointcloudId: typeof f.pointcloudId === "string" ? f.pointcloudId : null, kind: f.kind === "registration" || f.kind === "measurement" ? f.kind : null};
				this.folders.push(folder);
				this._byId.set(folder.id, folder);
				this._children.set(folder.id, []);
			}
			for(const folder of this.folders){
				if(folder.parentId !== null && !this._byId.has(folder.parentId)) folder.parentId = null;
			}
			for(const folder of this.folders){
				const seen = new Set([folder.id]);
				let p = folder.parentId;
				while(p !== null){
					if(seen.has(p)){ folder.parentId = null; break; }
					seen.add(p);
					p = this._byId.get(p).parentId;
				}
			}
		}

		const placed = new Set();
		const place = (container, key) => {
			if(placed.has(key)) return;
			if(key[0] === "d"){
				const folder = this._byId.get(key.slice(2));
				// en mappe hører hjemme i sin egen forælder
				if(!folder || this._folderContainer(folder.parentId) !== container) return;
			}else if(key[0] === "f"){
				if(container === TOP || !this.featureManager || !this.featureManager.get(key.slice(2))) return;
			}else return;
			placed.add(key);
			this._list(container).push(key);
		};
		if(data && data.order && typeof data.order === "object"){
			for(const [container, list] of Object.entries(data.order)){
				if(container !== TOP && container !== UNFILED && !this._byId.has(container)) continue;
				if(!Array.isArray(list)) continue;
				for(const key of list) if(typeof key === "string") place(container, key);
			}
		}
		// mapper uden gyldig plads -> sidst i deres forælder
		for(const folder of this.folders) place(this._folderContainer(folder.parentId), fkey(folder.id));
		// registreringer uden plads -> sidst i "Uden mappe"
		if(this.featureManager) for(const f of this.featureManager.features) place(UNFILED, ekey(f.id));

		if(data && typeof data.activeFolderId === "string" && this._byId.has(data.activeFolderId)) this.activeFolderId = data.activeFolderId;
		this._changed();
	}

	dispose(){
		if(!this.featureManager) return;
		this.featureManager.removeEventListener("feature_added", this._onFeatureAdded);
		this.featureManager.removeEventListener("feature_removed", this._onFeatureRemoved);
	}
}
